import { Frame } from './Frame';
import BlendMode from './BlendMode';
import { FrameType } from './FrameType';
import { filterCache, getSignalAt } from '../state/Store';
import { LayerData, FrameData, createMask, ShapeData } from './FlyerData';
import {
  LayerTimeline,
  PropertyTimeline,
  TimelinePoint,
  TIMELINE_VISIBILITY_KEY
} from './LayerTimeline';
import BackgroundStyle from './BackgroundStyle';
import { PictureFrame } from './PictureFrame';
import { SoundWaveFrame } from './SoundWaveFrame';
import { SignalSource } from './SignalSource';
import {
  BackgroundRepeat,
  Effect,
  EffectData,
  LayerContainer
} from '@beatflyer/shaders-lib';
import { BFAnimatedSprite } from 'utils/BFAnimatedSprite';
import {
  getShapePIXIRectangle,
  getShapeRectangle,
  transformShape
} from 'utils/GraphicsUtils';
import { CompositionEffect } from 'compose/CompositionEffect';
import { CaptionsFrame } from 'captions/CaptionsFrame';
import { createFrameFromData } from './FrameUtils';
import { cloneObject, getCloneBaseId, isUUID, newUUID } from 'utils';

const createEffect = require('@beatflyer/shaders-lib').createEffect;
const { PIXI } = window as any;

class Layer {
  id: string = newUUID();
  frames: Frame[] = [];
  effects: Effect[] = [];
  stageWidth: number = 0;
  stageHeight: number = 0;
  level: number = 0;
  syncPlayback: boolean = false;
  sprite = new LayerContainer();
  visible: boolean = true;
  locked: boolean = false;
  timeline: LayerTimeline = {
    visibility: new PropertyTimeline()
  };
  mask: ShapeData | null = null;
  compositionEffect: CompositionEffect | null = null;
  previewedCompositionEffect: CompositionEffect | null = null;

  private _needsUpdate: boolean = false;
  set needsUpdate(value: boolean) {
    if (!this.visible) {
      return;
    }
    this._needsUpdate = value;
  }
  get needsUpdate(): boolean {
    return this._needsUpdate;
  }

  private _blendMode: BlendMode = BlendMode.Normal;
  private _repeat: BackgroundRepeat = BackgroundRepeat.Mirror;
  private _pixelRatio: number = 1;
  private _visibleFrameIndex: number = 0;
  get visibleFrameIndex(): number {
    return this._visibleFrameIndex;
  }
  private _playbackPosition: number = 0;

  private _selectedFrame: Frame | null = null;
  set selectedFrame(frame: Frame | null) {
    this._selectedFrame = frame;
    if (frame) {
      this.compositionEffect?.reset();
      frame.sprite.visible = true;
    }
    this.updateVisibleFrame();
  }
  get selectedFrame(): Frame | null {
    return this._selectedFrame;
  }

  private _isEditingFrame = false;
  set isEditingFrame(value: boolean) {
    if (value === this._isEditingFrame) {
      return;
    }
    this._isEditingFrame = value;
    this.applyEffects();
  }
  get isEditingFrame(): boolean {
    return this._isEditingFrame;
  }

  private _maskRotation: number = 0;
  get maskRotation(): number {
    return this._maskRotation;
  }
  set maskRotation(value: number) {
    this._maskRotation = value;
    if (this.sprite?.mask !== null) {
      (this.sprite.mask as any).angle = value;
    }
  }

  setMaskBoundingBox(rect: PIXI.Rectangle) {
    if (this.mask) {
      const prevBox = getShapePIXIRectangle(this.mask);
      this.setMask(transformShape(prevBox, rect, this.mask));
    }
  }

  async load() {
    for (const frame of this.frames) {
      await frame.load();
    }
  }

  applyEffects() {
    let blendFilters: any[] = [];
    if (this._blendMode !== BlendMode.Normal) {
      const filter = PIXI.picture?.getBlendFilter?.(this._blendMode);
      if (filter) {
        blendFilters = [filter];
      } else {
        console.error(`Could not create blend mode filter`, this._blendMode);
      }
    }
    this.sprite.filters = this.effects
      .filter(filter => {
        filter.blendMode = BlendMode.Normal;
        return filter.visible && this.isEditingFrame === false;
      })
      .concat(blendFilters);
  }

  addFrame(frame: Frame, index?: number) {
    frame.load();
    if (this.frames.length) {
      if (frame.relativeDuration === 1) {
        frame.relativeDuration = 1 / (this.frames.length + 1);
      }
      const leftScale = 1 - frame.relativeDuration;
      this.frames.forEach(f => {
        f.relativeDuration *= leftScale;
      });
    }
    if (index !== undefined) {
      this.frames.splice(index, 0, frame);
    } else {
      this.frames.push(frame);
    }
    this.updateCurrentFrame();
    this.applyEffects();
    this.needsUpdate = true;
    if (frame.type === FrameType.AnimatedGIF) {
      frame.on('animated', this.gifDidAnimate);
    }
  }

  gifDidAnimate = () => {
    this.updateVisibleFrame();
    this.applyEffects();
    this.needsUpdate = true;
  };

  addEffect(effect: Effect, index: number = -1) {
    if (index > -1) {
      this.effects.splice(index, 0, effect);
    } else {
      this.effects.push(effect);
    }
    effect.cache = filterCache;
    this.applyEffects();
  }

  removeEffect(effect: Effect) {
    const index = this.effects.indexOf(effect);
    const effectToRemove = this.effects[index];
    const effectId = (effectToRemove.constructor as any).id;
    if (this.timeline[effectId]) {
      delete this.timeline[effectId];
    }
    this.effects.splice(index, 1);
    this.applyEffects();
    this.needsUpdate = true;
  }

  replaceFrame(oldFrame: Frame, newFrame: Frame) {
    const index = this.frames.indexOf(oldFrame);
    newFrame.relativeDuration = oldFrame.relativeDuration;
    this.frames.splice(index, 1, newFrame);
    oldFrame.dispose();
    this.updateCurrentFrame();
    this.applyEffects();
    this.needsUpdate = true;
    oldFrame.off('animated', this.gifDidAnimate);
  }

  removeFrame(frame: Frame) {
    const index = this.frames.indexOf(frame);
    const leftScale = 1 / (1 - frame.relativeDuration);
    this.frames.splice(index, 1);
    this.frames.forEach(f => {
      f.relativeDuration *= leftScale;
    });
    frame.dispose();
    if (frame === this.selectedFrame) {
      this._selectedFrame = null;
    }
    if (this.frames.length <= 1) {
      this.syncPlayback = false;
    }
    // if (!this.syncPlayback) {
    // update visible frame
    this.updateCurrentFrame();
    // this.sprite!.texture = this.frames[this._visibleFrameIndex].texture!;
    // this.centerSprite();
    this.applyEffects();
    this.needsUpdate = true;
    frame.off('animated', this.gifDidAnimate);
    // }
  }

  setStageSize(width: number, height: number) {
    const zoomX = width / this.stageWidth;
    const zoomY = height / this.stageHeight;
    this.stageWidth = width;
    this.stageHeight = height;
    this.sprite.filterArea = new PIXI.Rectangle(0, 0, width, height);
    this.frames.forEach(f => {
      f.stageWidth = width;
      f.stageHeight = height;
      if (f.type === FrameType.SolidColor || f.type === FrameType.Gradient) {
        f.boundingBox = new PIXI.Rectangle(0, 0, width, height);
      } else {
        if (f.type === FrameType.SoundWave) {
          f.sprite.filterArea = new PIXI.Rectangle(0, 0, width, height);
        }
        // move boundingBox
        const w = f.boundingBox.width;
        const h = f.boundingBox.height;
        const hw = w * 0.5;
        const hh = h * 0.5;
        const bcx = f.boundingBox.x + hw;
        const bcy = f.boundingBox.y + hh;
        const nbcx = bcx * zoomX;
        const nbcy = bcy * zoomY;
        f.boundingBox = new PIXI.Rectangle(nbcx - hw, nbcy - hh, w, h);
      }
    });
  }

  set blendMode(value: BlendMode) {
    if (value === this._blendMode) {
      return;
    }
    if (this.effects) {
      this.effects.forEach(effect => {
        effect.blendMode = value;
      });
    }
    this._blendMode = value;
    this.applyEffects();
    this.needsUpdate = true;
  }
  get blendMode(): BlendMode {
    return this._blendMode;
  }

  set repeat(value: BackgroundRepeat) {
    if (this.effects) {
      this.effects.forEach(effect => {
        effect.repeat = value;
      });
    }
    this._repeat = value;
    this.needsUpdate = true;
  }
  get repeat(): BackgroundRepeat {
    return this._repeat;
  }

  set pixelRatio(value: number) {
    if (this.effects) {
      this.effects.forEach(effect => {
        effect.pixelRatio = value;
      });
    }
    this._pixelRatio = value;
  }
  get pixelRatio(): number {
    return this._pixelRatio;
  }

  get isLoaded(): boolean {
    return this.currentFrame.isLoaded;
  }

  get isVideo(): boolean {
    return this.frames[0].isVideo;
  }

  update(
    position: number,
    opacity: number,
    level: number,
    spectrum: Uint8Array,
    waveform: Uint8Array,
    time: number,
    useLayerLevel: boolean = false
  ) {
    this._playbackPosition = position;
    this.level = level;
    let visibleFrame = this.selectedFrame || this.currentFrame;
    this.effects
      .filter(f => f.visible)
      .forEach(filter => {
        const effect = filter;
        if (!effect.isSplitEffect) {
          effect.opacity =
            [
              SignalSource.Full,
              SignalSource.Playback,
              SignalSource.PlaybackLoop,
              SignalSource.Seconds15Loop
            ].indexOf(effect.signalSource) > -1
              ? 1
              : opacity;
          if (effect.acceptsSpectrum) {
            if (spectrum && spectrum.length) {
              effect.update(time, spectrum, waveform);
            }
          } else {
            const { id } = effect.constructor as any;
            let factor = 1;
            const effectTimeline = this.timeline[id];
            if (effectTimeline) {
              factor = effectTimeline.getValueAt(position);
            }
            const effectLevel = useLayerLevel
              ? level
              : getSignalAt(effect.signalSource, position);
            effect.update(time, effectLevel * factor);
          }
        }
      });
    if (!this.selectedFrame) {
      if (this.syncPlayback) {
        if (this.frames.length > 1) {
          const frameIndex = Math.round(
            opacity * level * (this.frames.length - 1)
          );
          if (this._visibleFrameIndex !== frameIndex) {
            this._visibleFrameIndex = frameIndex;
            this.updateVisibleFrame();
          }
        }
      } else {
        this.setPlaybackPosition(position);
      }
      visibleFrame = this.currentFrame;
    }

    if (
      spectrum &&
      spectrum.length &&
      visibleFrame &&
      visibleFrame.type === FrameType.SoundWave
    ) {
      const { meshSpectrum } = visibleFrame as SoundWaveFrame;
      meshSpectrum.update(spectrum, waveform);
    }
    if (visibleFrame && visibleFrame.type === FrameType.Captions) {
      (visibleFrame as CaptionsFrame).update(time);
    }

    // set visibility based on timeline
    if (this.sprite) {
      this.sprite.alpha =
        this.timeline[TIMELINE_VISIBILITY_KEY].getValueAt(position);
    }
    if (visibleFrame.sprite instanceof BFAnimatedSprite) {
      visibleFrame.sprite.gotoAndStop(time, true);
    }
  }

  setPlaybackPosition(value: number) {
    if (this.frames.length < 2) {
      return;
    }
    this.updateCurrentFrame();
  }

  updateCurrentFrame() {
    if (this.frames.length <= 1) {
      this._visibleFrameIndex = 0;
    } else {
      const start = this.getTimelineStart();
      const end = this.getTimelineEnd();
      const duration = end - start;
      let p = start;
      this._visibleFrameIndex = 0;
      this.frames.forEach((frame, index) => {
        if (p < this._playbackPosition) {
          this._visibleFrameIndex = index;
        }
        p += frame.relativeDuration * duration;
      });
    }
    this.updateVisibleFrame();
  }

  updateVisibleFrame() {
    const visibleFrame = this.selectedFrame || this.currentFrame;
    if (!this.sprite.children.length) {
      this.sprite.addChild(visibleFrame.sprite);
      if (visibleFrame.sprite.mask) {
        this.sprite.addChild(visibleFrame.sprite.mask);
      }
    } else if (this.sprite.children[0] !== visibleFrame.sprite) {
      const previousHasMask = this.sprite.children[0].mask !== null;
      const removedChild = this.sprite.removeChildAt(0);
      this.frames.forEach(frame => {
        if (frame.sprite === removedChild) {
          frame.removedFromView();
        }
      });
      if (previousHasMask) {
        this.sprite.removeChildAt(0);
      }
      this.sprite.addChild(visibleFrame.sprite);
      if (visibleFrame.sprite.mask) {
        this.sprite.addChild(visibleFrame.sprite.mask);
      }
    }
  }

  // centerSprite() {
  //   const currentFrame = this.currentFrame;
  //   if (
  //     currentFrame.type === FrameType.Text ||
  //     currentFrame.type === FrameType.SolidColor
  //   ) {
  //     return;
  //   }
  //   // console.log( 'CENTER SPRITE ' + this.__visibleFrameIndex + ': ' + currentFrame.isLoaded );
  //   if (currentFrame.isLoaded) {
  //     // console.log('sw:' + this.stageWidth + ' sh:' + this.stageHeight + ' cw:' + currentFrame.width + ' ch:' + currentFrame.height );
  //     const scaleX = this.stageWidth / currentFrame.width;
  //     const scaleY = this.stageHeight / currentFrame.height;
  //     const scale = Math.max(scaleX, scaleY);
  //     const w = currentFrame.width * scale;
  //     const h = currentFrame.height * scale;
  //     this.sprite!.width = w;
  //     this.sprite!.height = h;
  //     this.sprite!.x = (this.stageWidth - w) * 0.5;
  //     this.sprite!.y = (this.stageHeight - h) * 0.5;
  //     // console.log('x:' + this.sprite.x + ' y:' + this.sprite.y + ' w:' + this.sprite.width + ' h:' + this.sprite.height);
  //     this.sprite!.alpha = this.timeline[TIMELINE_VISIBILITY_KEY].getValueAt(
  //       this._playbackPosition
  //     );
  //   } else {
  //     currentFrame.once('load', () => {
  //       // console.log('FRAME UPDATED LAYER');
  //       if (this.currentFrame === currentFrame) {
  //         this.centerSprite();
  //       }
  //     });
  //   }
  // }

  setMask(shape: ShapeData | null) {
    this.mask = null;
    if (this.sprite.mask) {
      this.sprite.mask.destroy(true);
    }
    this.sprite.mask = null;
    if (shape) {
      this.mask = shape;
      // @ts-ignore
      this.sprite.mask = createMask(shape);
      (this.sprite.mask as any).anchor.set(0.5);
      const rect = getShapeRectangle(shape);
      this.sprite.mask?.position.set(
        rect.x + rect.width / 2,
        rect.y + rect.height / 2
      );
      (this.sprite.mask as any).angle = this._maskRotation;
    } else {
      this._maskRotation = 0;
    }
    this.needsUpdate = true;
  }

  populateFromObj(data: LayerData) {
    if (data.id) {
      this.id = data.id;
    }
    this.stageWidth = data.stageWidth!;
    this.stageHeight = data.stageHeight!;
    this.sprite.filterArea = new PIXI.Rectangle(
      0,
      0,
      this.stageWidth,
      this.stageHeight
    );

    if (data.frames) {
      this.frames = data.frames.map((fr: FrameData) => {
        const frame = createFrameFromData(
          fr,
          this.stageWidth,
          this.stageHeight
        );
        if (frame.type === FrameType.AnimatedGIF) {
          frame.on('animated', this.gifDidAnimate);
        }
        return frame;
      });
    }

    if (this.frames.length > 1 && this.frames[0].relativeDuration === 1) {
      // it means no frame has relativeDuration, hence automatically assign it
      const eachRelativeDuration = 1 / this.frames.length;
      this.frames.forEach(f => {
        f.relativeDuration = eachRelativeDuration;
      });
    }

    this.updateCurrentFrame();
    this.applySettings(data);

    if (data.timeline) {
      const keys = Object.keys(data.timeline);
      for (const key of keys) {
        this.timeline[key] = PropertyTimeline.createFromData(
          data.timeline[key]
        );
      }
    }

    if (data.mask) {
      this.setMask(data.mask);
      if (data.maskRotation !== undefined) {
        this.maskRotation = data.maskRotation;
      }
    }

    if (data.compositionEffect) {
      const effect = CompositionEffect.createCompositionEffect(
        this,
        data.compositionEffect.id,
        data.compositionEffect
      );
      this.compositionEffect = effect;
    }
  }

  toObject(forDatabase: boolean = false): LayerData {
    const result: LayerData = {
      frames: this.frames.map(frame => frame.asData(forDatabase))
    };
    if (!isUUID(this.id)) {
      result.id = this.id;
    }

    if (this.syncPlayback) {
      result.syncPlayback = true;
    }

    if (!this.visible) {
      result.visible = false;
    }

    if (this.locked) {
      result.locked = true;
    }

    result.repeat = this._repeat;

    if (this._blendMode !== BlendMode.Normal) {
      result.blendMode = this._blendMode;
    }

    const effects: any[] = [];
    this.effects.forEach((effect: Effect) => {
      if (effect.isPreviewing) {
        return;
      }
      const effectData = effect.toObject();
      effectData.id = effect.id;
      effectData.signalSource = effect.signalSource;
      effects.push(effectData);
    });
    if (effects.length) {
      result.effects = effects;
    }

    if (this.hasCustomTimeline()) {
      result.timeline = {};
      const keys = Object.keys(this.timeline);
      for (const key of keys) {
        const timeline = this.timeline[key];
        if (timeline.isCustom()) {
          result.timeline[key] = timeline.asData();
        }
      }
    }

    if (this.mask) {
      result.mask = this.mask;
      if (this.maskRotation !== 0) {
        result.maskRotation = this.maskRotation;
      }
    }

    if (this.compositionEffect) {
      result.compositionEffect = this.compositionEffect.toObject();
    }

    return result;
  }

  applySettings(data: LayerData): void {
    if (data.syncPlayback !== undefined) {
      this.syncPlayback = data.syncPlayback;
    }

    if (data.visible !== undefined) {
      this.visible = data.visible;
    }

    if (data.locked !== undefined) {
      this.locked = data.locked;
    }

    if (data.repeat !== undefined) {
      this.repeat = data.repeat;
    }

    if (data.pixelRatio !== undefined) {
      this.pixelRatio = data.pixelRatio;
    }

    if (data.effects) {
      this.effects = data.effects.map((effect: EffectData) => {
        const filter = createEffect(effect) as Effect;
        filter.id = effect.id ?? newUUID();
        filter.layerTransform = this.sprite.filterTransform;
        filter.cache = filterCache;
        filter.repeat = this.repeat;
        filter.pixelRatio = this.pixelRatio;
        filter.signalSource = effect.signalSource || SignalSource.Default;
        return filter;
      });
    }

    if (data.blendMode !== undefined) {
      this._blendMode = data.blendMode;
    }
    this.applyEffects();

    this.needsUpdate = true;
  }

  containsFrameWithName(filename: string): boolean {
    for (let i = 0; i < this.frames.length; i++) {
      const f: Frame = this.frames[i];
      if (
        f instanceof PictureFrame &&
        (f as PictureFrame).filename === filename
      ) {
        return true;
      }
    }
    return false;
  }

  getFrameByFilename(filename: string): Frame | null {
    for (let i = 0; i < this.frames.length; i++) {
      const f: Frame = this.frames[i];
      if (
        f instanceof PictureFrame &&
        (f as PictureFrame).filename === filename
      ) {
        return f;
      }
    }
    return null;
  }

  get currentFrame(): Frame {
    return this.frames[this._visibleFrameIndex];
  }

  // get texture(): PIXI.Texture | null {
  //   return this.currentFrame.texture;
  // }

  get visibleEffectsCount(): number {
    let result = 0;
    if (this.effects) {
      this.effects.forEach(filter => {
        const effect: any = filter;
        if (effect.visible) {
          result++;
        }
      });
    }
    return result;
  }

  pause() {
    if (!this.isVideo) {
      return;
    }
    const video: HTMLVideoElement = this.frames[0]!.texture!.baseTexture
      .source as HTMLVideoElement;
    video.pause();
  }

  play(seconds: number): Promise<void> {
    if (!this.isVideo) {
      return Promise.resolve();
    }
    const video: HTMLVideoElement = this.frames[0]!.texture!.baseTexture
      .source as HTMLVideoElement;
    if (video && video.duration) {
      video.currentTime = seconds % video.duration;
      return video.play();
    }
    return Promise.resolve();
  }

  // setSprite(sprite: PIXI.Sprite) {
  //   if (this.sprite) {
  //     this.sprite.destroy();
  //   }
  //   this.sprite = sprite;
  //   this.sprite.filters = this.effects.filter(filter => {
  //     filter.blendMode = BlendMode.Normal;
  //     return filter.visible;
  //   });
  //   if (this.blendMode !== BlendMode.Normal) {
  //     const len = this.sprite.filters.length;
  //     if (len) {
  //       const lastFilter = this.sprite.filters[len - 1];
  //       lastFilter.blendMode = this.blendMode;
  //     } else {
  //       this.sprite.blendMode = this.blendMode;
  //     }
  //   }
  //   this.centerSprite();
  //   this.needsUpdate = false;
  // }

  hasTransparentFrame(): boolean {
    const len = this.frames.length;
    if (len) {
      for (let i = 0; i < len; i++) {
        const frame = this.frames[i];
        switch (frame.backgroundStyle) {
          case BackgroundStyle.Transparent:
          case BackgroundStyle.TransparentDark:
          case BackgroundStyle.TransparentLight:
            return true;
          default:
            break;
        }
      }
    }
    return false;
  }

  hasCustomTimeline(): boolean {
    const keys = Object.keys(this.timeline);
    for (const key of keys) {
      const timeline = this.timeline[key];
      if (timeline.isCustom()) {
        return true;
      }
    }
    return false;
  }

  cleanTimelines() {
    const keys = Object.keys(this.timeline);
    for (const key of keys) {
      if (key !== TIMELINE_VISIBILITY_KEY) {
        const timeline = this.timeline[key];
        if (!timeline.isCustom()) {
          delete this.timeline[key];
        }
      }
    }
  }

  getTimelineStart(): number {
    const { visibility } = this.timeline;
    // console.log(visibility.points);
    const leftPoint = visibility.points.find(p => p.value === 1);
    return leftPoint?.position || 0;
  }

  getTimelineEnd(): number {
    const { visibility } = this.timeline;
    const rightPoint = visibility.points
      .concat([])
      .reverse()
      .find(p => p.value === 1);
    return rightPoint?.position || 1;
  }

  setTimelineStartAndEnd(start: number, end: number) {
    console.log(`setTimelineStartAndEnd`, start, end);
    const { visibility } = this.timeline;
    const points: TimelinePoint[] = [];
    if (start > 0) {
      points.push({
        position: 0,
        value: 0
      });
      points.push({
        position: start,
        value: 0
      });
    }
    points.push({
      position: start,
      value: 1
    });
    points.push({
      position: end,
      value: 1
    });
    if (end < 1) {
      points.push({
        position: end,
        value: 0
      });
      points.push({
        position: 1,
        value: 0
      });
    }
    visibility.points = points;
  }

  resetGeomTransformation() {
    this.sprite.resetFilterTransform();
    this.sprite.resetCompositionTransform();
    // this.sprite.alpha = 1; // Not sure why this is not needed!
    this.frames.forEach(frame => {
      frame.sprite.transform.updateSkew();
      frame.sprite.transform._localID++;
      frame.sprite.visible = true;
    });
  }

  async clone(
    copyIndex: number,
    includeEffects: boolean = false
  ): Promise<Layer> {
    const clone = new Layer();
    clone.id = `${getCloneBaseId(this.id)}-${copyIndex}`;
    // clone.visible = this.visible;
    clone.syncPlayback = this.syncPlayback;
    clone.blendMode = this.blendMode;
    clone.repeat = this.repeat;
    clone.setStageSize(this.stageWidth, this.stageHeight);
    for (const frame of this.frames) {
      const clonedFrame = frame.clone(copyIndex);
      await clonedFrame.load();
      clone.frames.push(clonedFrame);
    }
    clone.updateCurrentFrame();
    if (this.mask) {
      clone.setMask(cloneObject(this.mask));
      if (this.maskRotation !== undefined) {
        clone.maskRotation = this.maskRotation;
      }
    }
    if (includeEffects) {
      if (this.compositionEffect) {
        const data = this.compositionEffect.toObject();
        clone.compositionEffect = CompositionEffect.createCompositionEffect(
          clone,
          data.id,
          data
        );
        clone.compositionEffect.id = `${this.compositionEffect.id}-${copyIndex}`;
      }
      clone.effects = this.effects.map(fx => {
        const data = fx.toObject();
        const newEffect = createEffect(data) as Effect;
        newEffect.id = `${fx.id}-${copyIndex}`;
        newEffect.layerTransform = clone.sprite.filterTransform;
        newEffect.cache = filterCache;
        newEffect.repeat = clone.repeat;
        newEffect.pixelRatio = clone.pixelRatio;
        newEffect.signalSource = data.signalSource || SignalSource.Default;
        return newEffect;
      });
    }
    clone.applyEffects();
    return clone;
  }

  dispose() {
    if (this.sprite) {
      this.sprite.mask?.destroy(true);
      this.sprite.destroy();
      // this.sprite = null;
    }
    this.frames.forEach(frame => {
      frame.dispose();
    });
    this.frames = [];
    this.effects = [];
  }
}

export default Layer;



// WEBPACK FOOTER //
// ./src/data/Layer.ts